/**
 * Debug utilities for authentication troubleshooting
 */

export const debugAuth = {
  /**
   * Log all localStorage keys related to Cognito
   */
  logCognitoTokens: () => {
    logger.debug('🔍 Cognito tokens in localStorage:');
    const keys = Object.keys(localStorage);
    const cognitoKeys = keys.filter(key => 
      key.includes('CognitoIdentityServiceProvider') || 
      key.includes('amplify') ||
      key.includes('idToken') ||
      key.includes('accessToken')
    );
    
    cognitoKeys.forEach(key => {
      const value = localStorage.getItem(key);
      logger.debug(`📋 ${key}:`, value?.substring(0, 100) + '...');
    });
  },

  /**
   * Decode and log JWT token payload
   */
  decodeToken: (token) => {
    try {
      if (!token) {
        logger.debug('❌ No token provided');
        return null;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      logger.debug('🔓 Token payload:', payload);
      return payload;
    } catch (error) {
      logger.error('❌ Error decoding token:', error);
      return null;
    }
  },

  /**
   * Check current authentication state
   */
  checkAuthState: async () => {
    try {
      const { fetchAuthSession } = await import('@aws-amplify/auth');
      const session = await fetchAuthSession();
      
      logger.debug('🔍 Current auth session:');
      logger.debug('✅ Has tokens:', !!(session.tokens));
      logger.debug('✅ Has access token:', !!(session.tokens?.accessToken));
      logger.debug('✅ Has ID token:', !!(session.tokens?.idToken));
      
      if (session.tokens?.idToken) {
        const payload = debugAuth.decodeToken(session.tokens.idToken.toString());
        logger.debug('👥 Groups from ID token:', payload?.['cognito:groups']);
        logger.debug('📧 Email from ID token:', payload?.email);
        logger.debug('🏷️ Custom Group:', payload?.['custom:Group']);
      }
      
      return session;
    } catch (error) {
      logger.error('❌ Error checking auth state:', error);
      return null;
    }
  },

  /**
   * Clear all authentication data
   */
  clearAllAuthData: () => {
    logger.debug('🧹 Clearing all authentication data...');
    const keys = Object.keys(localStorage);
    const authKeys = keys.filter(key => 
      key.includes('CognitoIdentityServiceProvider') || 
      key.includes('amplify') ||
      key.includes('auth')
    );
    
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      logger.debug(`🗑️ Removed: ${key}`);
    });
    
    logger.debug('✅ All auth data cleared');
  },

  /**
   * Test logout functionality
   */
  testLogout: async () => {
    try {
      logger.debug('🚪 Testing logout...');
      const { performLogout } = await import('./logoutUtils');
      
      await performLogout();
      logger.debug('✅ Logout test completed');
    } catch (error) {
      logger.error('❌ Logout test failed:', error);
    }
  },

  /**
   * Test emergency logout
   */
  testEmergencyLogout: () => {
    try {
      logger.debug('🚨 Testing emergency logout...');
      const { emergencyLogout } = require('./logoutUtils');
      
      emergencyLogout();
      logger.debug('✅ Emergency logout test completed');
    } catch (error) {
      logger.error('❌ Emergency logout test failed:', error);
    }
  }
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.debugAuth = debugAuth;
}

export default debugAuth;
